import { Folder } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { FolderPickerList } from "src/components/Modals/FolderPickerList";
import { ShortLimitedAccess } from "src/components/NewComponents/LimitedAccess";
import { hasLimitedAccess } from "src/helpers/getPermissions";
import { useFolderPicker } from "src/hooks/useFolderPicker";

interface SelectFoldersModalProps {
	onClose: () => void;
	onSelect: (folderIds: string[]) => void;
	initialSelectedIds?: string[];
}

export const SelectFoldersModal = ({
	onClose,
	onSelect,
	initialSelectedIds = [],
}: SelectFoldersModalProps) => {
	const { t } = useTranslation();
	const {
		selectedFolderIds,
		folders,
		isLoading,
		showCreateModal,
		isCreatingFolder,
		setShowCreateModal,
		toggleFolderSelection,
		handleCreateFolder,
	} = useFolderPicker({ initialSelectedIds, multiSelect: true });

	const title = t("modals.selectFolders.title", "Wybierz foldery");
	const selectedNames = folders
		.filter((f) => selectedFolderIds.includes(String(f.id)))
		.map((f) => f.name)
		.join(", ");

	const handleConfirm = () => {
		onSelect(selectedFolderIds);
		onClose();
	};

	if (hasLimitedAccess()) {
		return (
			<Modal onClose={onClose} className="modal--fixed-footer">
				<ModalHeader Icon={Folder} title={title} hasCloseButton />
				<ShortLimitedAccess />
			</Modal>
		);
	}

	return (
		<Modal onClose={onClose} className="modal--fixed-footer">
			{!showCreateModal && (
				<ModalHeader Icon={Folder} title={title} hasCloseButton />
			)}
			<FolderPickerList
				folders={folders}
				isLoading={isLoading}
				selectedFolderIds={selectedFolderIds}
				onToggle={toggleFolderSelection}
				showCreateModal={showCreateModal}
				isCreatingFolder={isCreatingFolder}
				onOpenCreateModal={() => setShowCreateModal(true)}
				onCloseCreateModal={() => setShowCreateModal(false)}
				onCreateFolder={handleCreateFolder}
				folderTestId="select-folders-item"
				createButtonTestId="select-folders-create-button"
				onClearAll={() => {
					selectedFolderIds.forEach((id) => {
						toggleFolderSelection(id);
					});
				}}
			/>
			{!showCreateModal && (
				<ModalFooter className="justify-between items-center gap-12 mt-16 pt-16 border-top-1">
					<span className="size-14-light text-grey">
						{selectedNames ? (
							<Trans
								i18nKey="folder.selectedFolders"
								defaults="Wybrane: <0>{{names}}</0>"
								values={{ names: selectedNames }}
								components={[<strong key="0" className="text-black" />]}
							/>
						) : (
							t("folder.chooseFolders", "Wybierz foldery z listy")
						)}
					</span>
					<div className="flex gap-12">
						<TextPrimaryButton onClick={onClose}>
							{t("commands.cancel", "Anuluj")}
						</TextPrimaryButton>
						<PrimaryButton
							data-test-id="select-folders-confirm-button"
							onClick={handleConfirm}
						>
							{t("modals.selectFolders.confirm", "Wybierz")}
						</PrimaryButton>
					</div>
				</ModalFooter>
			)}
		</Modal>
	);
};
